<script setup>
  import {computed, inject, ref, watch} from "vue";

  import ComponentCategoryBadge from "@components/Badges/CategoryBadge.vue";
  import ComponentContactCard from "@components/Cards/ContactCard.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentEmptyState from "@components/EmptyState/EmptyState.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import ComponentInput from "@components/Inputs/Input.vue";
  import ComponentSelect from "@components/Selects/Select.vue";
  import {useForm} from "@inertiajs/vue3";
  import ContactsContactForm from "@pages/Contact/Components/ContactForm.vue";
  import {assignmentRole} from "@pages/Contact/Enums/Enums.js";
  import {MedButton, MedInfoButton, MedSpinner} from "@pharma4u/medicheck-components";

  import {useAuthHttp} from "@/resources/js/Composable/useAuthHttp.js";

  defineOptions({
    name: "ComponentPatientAddressBookDialog",
  });

  const props = defineProps({
    patient: {
      type: Object,
      required: true,
    },
  });

  const broadcast = inject("$broadcast");

  const refPatientAddressBookDialog = ref(null);

  const form = useForm({
    role: null,
  });

  const addressBook = ref([]);
  const contactSearch = useAuthHttp();

  const addressBookSearch = ref(null);
  const selectedContact = ref(null);
  const addNewContact = ref(false);
  const addNewContactDefaultValues = ref(null);

  const filteredAddressBook = computed(() => {
    if (addressBookSearch.value === null || addressBookSearch.value.length === 0) {
      return addressBook.value;
    }

    return (
      addressBook.value.filter((contact) => {
        let searchString = [contact.firstname, contact.lastname, contact.company].join(" ").toLowerCase();
        return addressBookSearch.value
          .replace("Dr.", "")
          .replace("Prof.", "")
          .replace("Med.", "")
          .toLowerCase()
          .split(" ")
          .every((word) => searchString.includes(word));
      }) ?? []
    );
  });

  const showNoContactsFound = computed(() => {
    return filteredAddressBook.value?.length === 0 && !contactSearch.processing;
  });

  const selectedContactAlreadyAdded = computed(() => isAlreadyAdded(selectedContact.value));

  watch(addressBookSearch, (newValue) => {
    // select first search result when searching in addressbook
    if (newValue === null || newValue.length === 0) {
      selectedContact.value = null;
    } else {
      selectedContact.value = filteredAddressBook.value[0];
    }
  });

  const isSelectedContact = (contact) => {
    return selectedContact.value !== null && selectedContact.value?.id === contact.id;
  };

  const isAlreadyAdded = (contact) => {
    return props.patient?.contacts?.some((pC) => pC.id === contact?.id);
  };

  const open = (search = null) => {
    contactSearch.post(route("api.contacts.search"), {
      onSuccess: (response) => {
        addressBook.value = response?.data ?? [];

        if (typeof search === "string") {
          addressBookSearch.value = search.replace("Dr.", "").replace("Prof.", "").replace("Med.", "").trim();
        } else if (typeof search === "object" && search !== null) {
          addNewContactDefaultValues.value = search;

          addressBookSearch.value = [search.company ?? search.firstname ?? null, search.lastname ?? null]
            .filter((p) => {
              return p !== null;
            })
            .join(" ");
        }
      },
    });

    refPatientAddressBookDialog.value.open();
  };

  const closeDialog = () => {
    refPatientAddressBookDialog.value.close();
    resetDialog();
  };

  const save = () => {
    form
      .transform((data) => ({
        ...data,
        contact_id: selectedContact.value?.id,
      }))
      .post(route("assignment.store", {patient: props.patient.id}), {
        preserveScroll: true,
        onSuccess: () => {
          resetDialog();
          broadcast.patient.postMessage(props.patient.id, {action: "reload.patient"});
        },
      });
  };

  const resetDialog = () => {
    selectedContact.value = null;
    form.role = null;
    addressBookSearch.value = null;
    addNewContact.value = false;
    addNewContactDefaultValues.value = null;
  };

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog
    ref="refPatientAddressBookDialog"
    :dialog-width="addNewContact ? 'large' : 'medium'"
    dialog-type="emptyActions"
    dialog-title="Kontakt verknüpfen"
    primary-label="Hinzufügen"
    :test-id="getTestId('address-book')"
  >
    <template #content>
      <div v-if="contactSearch.processing" class="grid min-h-32 w-full place-content-center">
        <MedSpinner size="xl" :data-test="getTestId('address-book-dialog-spinner')" />
      </div>

      <div v-else-if="addNewContact" class="col-span-4">
        <ContactsContactForm
          class="-mb-4 border-none shadow-none"
          :patient-id="patient.id"
          :redirect-back="true"
          :embedded-form="true"
          :contact="addNewContactDefaultValues"
          :emit-cancel="true"
          @select-existing-contact="
            (contact) => {
              selectedContact = contact;
              save();
              closeDialog();
            }
          "
          @submit-has-no-errors="closeDialog()"
          @cancel-button-clicked="closeDialog()"
        />
      </div>

      <div v-else class="grid grid-cols-6">
        <div class="col-span-2">
          <div v-if="!addNewContact">
            <ComponentInput
              v-model="addressBookSearch"
              autocomplete="off"
              class="max-w-52"
              placeholder="Suchen"
              :test-id="getTestId('address-book-dialog-search')"
              @clear="addNewContactDefaultValues = null"
            >
              <template #icon>
                <ComponentIcon class="text-xl text-gray-600">search</ComponentIcon>
              </template>
            </ComponentInput>

            <h2 class="mt-2 font-semibold">Adressbuch</h2>

            <div class="h-72 overflow-x-hidden overflow-y-auto overscroll-contain pb-2">
              <ul v-if="filteredAddressBook?.length > 0" class="space-y-2">
                <li
                  v-for="contact in filteredAddressBook"
                  :key="contact.id"
                  class="group rounded-md px-2 py-1 hover:bg-gray-100"
                  :class="{'bg-gray-100': isSelectedContact(contact)}"
                >
                  <button
                    class="flex w-full items-center justify-between"
                    :data-test="getTestId('address-book-dialog-contact-button')"
                    @focus="selectedContact = contact"
                    @mouseover="selectedContact = contact"
                    @click="selectedContact = contact"
                  >
                    <span class="flex flex-col items-start space-y-0.5 text-left">
                      <span v-if="contact.firstname || contact.lastname">
                        {{ contact.title }} {{ contact.firstname }} {{ contact.lastname }}
                      </span>

                      <span v-else>{{ contact.company }}</span>

                      <ComponentCategoryBadge v-if="isAlreadyAdded(contact)" color="green" label="Bereits verknüpft" />
                    </span>

                    <ComponentIcon
                      class="invisible text-gray-600 group-hover:visible"
                      :class="{'visible!': isSelectedContact(contact)}"
                    >
                      chevron_right
                    </ComponentIcon>
                  </button>
                </li>
              </ul>

              <div v-else-if="showNoContactsFound">
                <ComponentEmptyState class="mb-5" :test-id="getTestId('address-book-dialog-nothing-found')">
                  Keine Kontakte gefunden.
                </ComponentEmptyState>
              </div>
            </div>
          </div>
        </div>

        <div v-if="selectedContact" class="col-span-4 px-4">
          <ComponentContactCard :contact="selectedContact" />

          <ComponentSelect
            v-if="!selectedContactAlreadyAdded"
            v-model="form.role"
            class="mt-4"
            label="Funktion (optional)"
            :options="assignmentRole"
            :nullable="true"
            placeholder-text=""
            helper-text="Welche Rolle nimmt dieser Kontakt ein?"
            :test-id="getTestId('address-book-dialog-role')"
          />
        </div>

        <div v-else-if="showNoContactsFound" class="col-span-4 px-4">
          <MedButton
            icon="add"
            flat
            :data-test="getTestId('address-book-dialog-new-contact-button')"
            @click="addNewContact = true"
          >
            Neuer Kontakt
          </MedButton>
        </div>
      </div>
    </template>

    <template v-if="!addNewContact" #actions>
      <div class="mt-4 flex w-full flex-row-reverse flex-wrap justify-between">
        <div class="flex flex-row-reverse space-x-3 space-x-reverse">
          <MedButton
            variant="primary"
            :disabled="
              form.processing || selectedContactAlreadyAdded || showNoContactsFound || selectedContact === null
            "
            :data-test="getTestId('address-book-dialog-add-button')"
            @click="save()"
          >
            Hinzufügen
          </MedButton>

          <MedButton :data-test="getTestId('address-book-dialog-close-button')" @click="closeDialog()">
            Abbrechen
          </MedButton>
        </div>

        <MedButton
          v-if="!showNoContactsFound"
          icon="add"
          flat
          :data-test="getTestId('address-book-dialog-create-contact-button')"
          @click="addNewContact = true"
        >
          Neuer Kontakt
        </MedButton>
      </div>
    </template>

    <template #info-icon>
      <MedInfoButton
        size="lg"
        side="top"
        aria-label="Adressbuch Info"
        disable-portal
        :data-test="getTestId('address-book-dialog-info-button')"
      >
        <p class="text-sm">
          Stellen Sie eine Verbindung zwischen dem Patienten und Ärzten, Pflegediensten oder Heimen aus Ihrem Adressbuch
          her.
        </p>
      </MedInfoButton>
    </template>
  </ComponentDialog>
</template>
